import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Stack,
  Switch,
  Input,
  Button,
  IconButton,
  Icon,
  Tooltip,
} from "@chakra-ui/react";
import { BiMenu, BiX } from "react-icons/bi";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { cloneDeep, isEqual } from "lodash";
import { uniqueId } from "../../../_utils/uniqueId";
import { trackBasicEvent } from "../../../_utils/eventsTracker";
import { translate as trans } from "../Trans/Trans";
import ConfirmDangerButton from "../ConfirmDanger/ConfirmDangerButton";
import PostCarouselItem from "./PostCarouselItem";

const customFieldRowStyles = {
  display: "flex",
  alignItems: "center",
  maxWidth: "40rem",
  marginTop: "1",
  marginBottom: "1",
};

const customFieldLabelStyles = {
  flex: "0 0 auto",
  width: "25%",
  minWidth: "7rem",
  paddingRight: "1",
  fontSize: "sm",
  lineHeight: "1.1",
};

const customFieldContentStyles = {
  flex: "1 1 auto",
  display: "flex",
  alignItems: "center",
};

const emptyItem = {
  link: "",
  title: "",
  description: "",
  image: "",
};

const PostCarousel = ({ data, limit, onRemove, onChange }) => {
  const [elements, setElements] = useState();

  const handleRemove = useCallback(() => {
    onRemove?.();

    trackBasicEvent("post-carousel", "click", "remove");
  }, [onRemove, trackBasicEvent]);

  const handleItemChange = useCallback(
    ($id) => (values) => {
      setElements([
        ...elements.map((item) =>
          item.$id === $id ? { ...item, ...values } : item
        ),
      ]);
    },
    [elements, setElements]
  );

  const handleItemRemove = useCallback(
    ($id) => () => {
      setElements([...elements.filter((item) => item.$id !== $id)]);

      trackBasicEvent("post-carousel", "click", "item remove");
    },
    [elements, setElements, trackBasicEvent]
  );

  const handleItemAdd = useCallback(() => {
    const newItem = elements?.length
      ? elements[elements.length - 1]
      : emptyItem;

    setElements([
      ...elements,
      cloneDeep({
        ...newItem,
        $id: uniqueId(),
      }),
    ]);

    trackBasicEvent("post-carousel", "click", "item add");
  }, [elements, setElements, uniqueId, trackBasicEvent]);

  const handleEndToggleClick = useCallback(() => {
    const value = !data?.endCard?.value;

    onChange?.({
      endCard: value,
    });

    trackBasicEvent(
      "post-carousel",
      "click",
      `end card ${value ? "on" : "off"}`
    );
  }, [data, onChange, trackBasicEvent]);

  const handleEndCardLinkChange = useCallback(
    (event) => {
      onChange?.({
        endCardLink: event.target.value,
      });
    },
    [onChange]
  );

  const handleDragEnd = useCallback(
    ({ source, destination }) => {
      if (!source || !destination) return;
      if (source.droppableId !== destination.droppableId) return;
      if (source.index === destination.index) return;

      const newElements = cloneDeep(elements);
      const [reorderedItem] = newElements.splice(source.index, 1);

      newElements.splice(destination.index, 0, reorderedItem);

      setElements([...newElements]);

      trackBasicEvent("post-carousel", "click", "item move");
    },
    [elements, setElements, trackBasicEvent]
  );

  useEffect(() => {
    const newElements = cloneDeep(data?.items?.value?.slice(0, limit) || []);

    if (!newElements.length) {
      newElements.push(cloneDeep(emptyItem));
    }

    setElements(
      newElements.map((item) => ({ ...item, $id: item.$id || uniqueId() }))
    );
  }, [data?.items?.value]);

  useEffect(() => {
    if (elements) {
      if (!isEqual(data?.items?.value, elements)) {
        onChange?.({
          items: elements,
        });
      }
    }
  }, [elements]);

  return (
    <Box>
      <Box marginBottom="2" color="white">
        <Box display="flex">
          <Stack
            direction="row"
            align="center"
            cursor="pointer"
            padding="1"
            width="auto"
            onClick={handleEndToggleClick}
          >
            <Switch
              size="sm"
              colorScheme="primary"
              isChecked={!!data?.endCard?.value}
              isReadOnly
              pointerEvents="none"
            />
            <Box fontSize="sm">{trans("post_carousel.end_card.title")}</Box>
          </Stack>
          <Box marginLeft="auto">
            <Tooltip
              label={trans("post_carousel.action.delete")}
              hasArrow
              placement="top"
              shouldWrapChildren
            >
              <ConfirmDangerButton
                as={IconButton}
                icon={<Icon fontSize="xl" as={BiX} />}
                size="sm"
                variant="ghost"
                confirmTitle={trans(
                  "post_carousel.action.delete_confirmation_title"
                )}
                confirmMessage={trans(
                  "post_carousel.action.delete_confirmation"
                )}
                confirmButtonText={trans("generic.delete")}
                onConfirm={handleRemove}
              />
            </Tooltip>
          </Box>
        </Box>
        {data?.endCardLink ? (
          <Box sx={customFieldRowStyles}>
            <Box sx={customFieldLabelStyles}>
              {trans("post_carousel.end_card_link.title")}
            </Box>
            <Box sx={customFieldContentStyles}>
              <Input
                size="sm"
                marginLeft="1"
                value={data?.endCardLink?.value}
                onChange={handleEndCardLinkChange}
                borderColor={data?.endCardLink?.isInvalid ? "red.500" : null}
              />
            </Box>
          </Box>
        ) : null}
      </Box>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="carousel-items">
          {(droppableProvided) => (
            <Box
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
            >
              {elements?.map((item, index) => (
                <Draggable key={item.$id} draggableId={item.$id} index={index}>
                  {(draggableProvided) => (
                    <Box
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      position="relative"
                      marginBottom="2"
                    >
                      <Box
                        {...draggableProvided.dragHandleProps}
                        position="absolute"
                        top="0"
                        left="0"
                        zIndex="1"
                        padding="1"
                        color="white"
                      >
                        <Icon as={BiMenu} fontSize="lg" />
                      </Box>
                      <PostCarouselItem
                        data={item}
                        onRemove={handleItemRemove(item.$id)}
                        onChange={handleItemChange(item.$id)}
                      />
                    </Box>
                  )}
                </Draggable>
              ))}
              {droppableProvided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>
      {elements?.length < limit ? (
        <Box display="flex" justifyContent="center" marginBottom="2">
          <Button
            variant="outline"
            size="sm"
            disabled={data?.items?.value?.length >= limit}
            onClick={handleItemAdd}
          >
            {trans("post_carousel.action.add_next")}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};

PostCarousel.propTypes = {
  data: PropTypes.shape({
    endCard: PropTypes.shape({
      value: PropTypes.bool,
    }),
    endCardLink: PropTypes.shape({
      value: PropTypes.string,
      isInvalid: PropTypes.bool,
    }),
    items: PropTypes.shape({
      value: PropTypes.arrayOf(
        PropTypes.shape({
          link: PropTypes.string,
          title: PropTypes.string,
          description: PropTypes.string,
          image: PropTypes.string,
        })
      ),
    }),
  }),
  limit: PropTypes.number,
  onRemove: PropTypes.func,
  onChange: PropTypes.func,
};

PostCarousel.defaultProps = {
  data: undefined,
  limit: undefined,
  onRemove: undefined,
  onChange: undefined,
};

export default PostCarousel;
